import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Table, 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  Space, 
  Popconfirm,
  message,
  Card,
  Row,
  Col,
  Typography,
  Tooltip,
  Tag,
  Switch
} from 'antd';
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  SettingOutlined,
  TeamOutlined,
  UserOutlined,
  AppstoreOutlined
} from '@ant-design/icons';
import Api from "../../../services/api";

const { Title } = Typography;
const { Option } = Select;

function StudioUser() {
  const { studio } = useParams(); // Lấy studio name từ URL
  const navigate = useNavigate();
  const [data, setData] = useState({});
  const [user, setUser] = useState({});
  const [modal, setModal] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [studioId, setStudioId] = useState(null);
  const [form] = Form.useForm();

  const handleChangePage = (page) => {
    setPage(page);
  }

  // Function để lấy studio_id từ studio_name
  const getStudioId = async () => {
    try {
      const result = await Api.get('/api/studios');
      if (result?.data?.code === 0) {
        const studios = result.data.data;
        const foundStudio = studios.find(s => s.name === decodeURIComponent(studio));
        if (foundStudio) {
          setStudioId(foundStudio.id);
          return foundStudio.id;
        }
      }
    } catch (error) {
      console.error('Error fetching studio ID:', error);
    }
    return null;
  }

  const getData = async () => {
    if (!studioId) return;
    
    setLoading(true);
    Api.get(`/api/studio/${studioId}/users?page=${page}`)
      .then(result => {
        if (result?.data?.code == 0) {
          setData(result.data);
        }
        setLoading(false)
      })
      .catch(error => {
        setLoading(false);
      });
  }


  const handleEditUser = (item) => {
    setUser({
      id: item.id,
      name: item.name,
      email: item.email,
      status: item.status
    });
    form.setFieldsValue({
      name: item.name,
      email: item.email,
      status: item.status
    });
    setModal(true);
  }

  const saveData = () => {
    form.validateFields().then(values => {
      setLoading(true);
      const api = user?.id ? Api.put(`/api/studio/${studioId}/users/${user.id}`, {
        name: values.name,
        email: values.email,
        status: values.status
      }) : Api.post(`/api/studio/${studioId}/users`, {
        name: values.name,
        email: values.email,
        status: values.status
      })
      
      api.then(result => {
        setLoading(false)
        if (result.data.code == 0) {
          message.success(user?.id ? 'Cập nhật user thành công!' : 'Tạo user thành công!');
          getData()
          setUser({})
          setModal(false)
          form.resetFields();
        } else {
          // Hiển thị lỗi chi tiết từ backend
          const errorMessages = result.data.messages || ['Có lỗi xảy ra!'];
          errorMessages.forEach(msg => {
            if (Array.isArray(msg)) {
              msg.forEach(m => message.error(m));
            } else {
              message.error(msg);
            }
          });
        }
      }).catch(error => {
        setLoading(false);
        message.error('Có lỗi xảy ra!');
      });
    });
  }

  const handleDelete = (item) => {
    setLoading(true);
    Api.delete(`/api/studio/${studioId}/users/${item.id}`)
      .then(result => {
        setLoading(false);
        if (result.data.code == 0) {
          message.success('Xóa user thành công!');
          getData()
        } else {
          message.error('Có lỗi xảy ra!');
        }
      }).catch(error => {
        setLoading(false);
        message.error('Có lỗi xảy ra!');
      });
  }

  const handlePermissionApp = (item) => {
    // Chuyển sang trang phân quyền app cho user studio
    navigate(`/organisations/${studio}/user/${item.id}/permission`);
  }

  const columns = [
    {
      title: 'Tên User',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => {
        if (!text || !record) return null;
        return (
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <span style={{ fontWeight: 'bold' }}>{text}</span>
          </Space>
        );
      },
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => {
        if (!text) return null;
        return <span style={{ color: '#666' }}>{text}</span>;
      },
    },
    {
      title: 'Quyền',
      key: 'role_label',
      render: (_, record) => {
        const isOwner = Array.isArray(record.roles) && record.roles.some(r => String(r.code || '').toLowerCase().includes('owner'));
        return (
          <Tag color={isOwner ? 'gold' : 'default'}>
            {isOwner ? 'Owner studio' : 'User'}
          </Tag>
        );
      },
    },
    {
      title: 'Apps',
      key: 'app_count',
      render: (_, record) => {
        if (!record) return null;
        return (
          <Tag color="blue">
            <AppstoreOutlined style={{ marginRight: '4px' }} />
            {Array.isArray(record.app_permissions) ? record.app_permissions.length : 0} app
          </Tag>
        );
      },
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        if (!status) return null;
        return (
          <Tag color={status === 'ACTIVE' ? 'green' : 'red'}>
            {status === 'ACTIVE' ? 'Hoạt động' : 'Tạm khóa'}
          </Tag>
        );
      },
    },
    {
      title: 'Hành động',
      key: 'action',
      render: (_, record) => {
        if (!record) return null;
        return (
          <Space size="middle">
            <Tooltip title="Chỉnh sửa">
              <Button 
                type="default" 
                icon={<EditOutlined />} 
                size="small"
                onClick={() => handleEditUser(record)}
              />
            </Tooltip>
            <Tooltip title="Phân quyền App">
              <Button 
                type="primary" 
                icon={<AppstoreOutlined />} 
                size="small"
                onClick={() => handlePermissionApp(record)}
              />
            </Tooltip>
            <Tooltip title="Xóa">
              <Popconfirm
                title="Bạn có chắc chắn muốn xóa user này?"
                description="User sẽ bị xóa khỏi studio."
                onConfirm={() => handleDelete(record)}
                okText="Có"
                cancelText="Không"
              >
                <Button 
                  type="primary" 
                  danger 
                  icon={<DeleteOutlined />} 
                  size="small"
                />
              </Popconfirm>
            </Tooltip>
          </Space>
        );
      },
    },
  ];

  useEffect(() => {
    if (studio) {
      getStudioId().then(studioId => {
        if (studioId) {
          getData();
        }
      });
    }
  }, [studio]);

  useEffect(() => {
    if (studioId) {
      getData();
    }
  }, [page, studioId]);

  return (
    <>
      <div className="background-second-dashboard">
        <section className="main__content-dashbard">
          <div className="container-fluid">
            <Card>
              <Row justify="space-between" align="middle" style={{ marginBottom: '24px' }}>
                <Col>
                  <Title level={4} style={{ margin: 0, fontSize: '18px' }}>
                    <TeamOutlined style={{ marginRight: '8px', color: '#1890ff' }} />
                    Quản lý User
                  </Title>
                </Col>
                <Col>
                  <Button 
                    type="primary" 
                    icon={<PlusOutlined />}
                    onClick={() => {
                      setUser({});
                      form.resetFields();
                      setModal(true);
                    }}
                    size="small"
                  >
                    Tạo User
                  </Button>
                </Col>
              </Row>


              <Table
                columns={columns}
                dataSource={data?.data?.data || []}
                rowKey="id"
                loading={loading}
                bordered
                pagination={{
                  current: page,
                  total: data?.data?.total || 0,
                  pageSize: data?.data?.per_page || 10,
                  onChange: handleChangePage,
                  showSizeChanger: false,
                  showQuickJumper: false,
                  showTotal: (total, range) => 
                    `${range[0]}-${range[1]} của ${total} user`,
                }}
                scroll={{ x: 1000 }}
              />
            </Card>
          </div>
        </section>
      </div>

      <Modal
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            {user?.id ? 'Chỉnh sửa User Studio' : 'Tạo User Studio mới'}
          </Space>
        }
        open={modal}
        onCancel={() => {
          setModal(false);
          form.resetFields();
        }}
        onOk={saveData}
        confirmLoading={loading}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Form.Item
            label="Tên User"
            name="name"
            rules={[{ required: true, message: 'Vui lòng nhập tên user!' }]}
          >
            <Input placeholder="Nhập tên user" />
          </Form.Item>
          
          <Form.Item
            label="Email"
            name="email"
            rules={[
              { required: true, message: 'Vui lòng nhập email!' },
              { type: 'email', message: 'Email không hợp lệ!' }
            ]}
          >
            <Input placeholder="Nhập email" />
          </Form.Item>

          <Form.Item
            label="Trạng thái"
            name="status"
            initialValue="ACTIVE"
            valuePropName="checked"
            getValueFromEvent={(checked) => checked ? 'ACTIVE' : 'INACTIVE'}
            getValueProps={(value) => ({ checked: value === 'ACTIVE' })}
          >
            <Switch 
              checkedChildren="Hoạt động" 
              unCheckedChildren="Tạm khóa" 
            />
          </Form.Item>
          
        </Form>
      </Modal>
    </>
  );
}

export default StudioUser;
